'use client';

import * as React from 'react';
import { useId, useMemo, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';

import { cn } from '../lib/utils';
import type { AutocompleteOption } from './autocomplete-types';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

/** Same shape as `AutocompleteOption`, so a migration only swaps the tag. */
export type OptionPickerOption = AutocompleteOption;

export interface OptionPickerProps {
  /** Committed value. `null`, `undefined` and '' all mean "nothing chosen". */
  value?: string | null;
  /**
   * Called only when the user chooses an option (or the clear row, with '').
   * Never with search text.
   */
  onChange: (value: string) => void;
  options: OptionPickerOption[];
  /** Trigger text when nothing is chosen. */
  placeholder?: string;
  /** Placeholder (and accessible name) of the search box inside the popover. */
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  /** Error message; also marks the trigger `aria-invalid`. */
  error?: string;
  /** Context line rendered above the search box inside the popover. */
  header?: React.ReactNode;
  /**
   * Offer a row that empties the value, while one is chosen and the search box
   * is empty (so Enter after typing always picks a match, never the clear row).
   */
  clearable?: boolean;
  /** Label of that row. */
  clearLabel?: string;
  className?: string;
  /** Trigger id, for an external `<label htmlFor>`, which then names it. */
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /**
   * From `FormControl` (its description and message ids). Merged with this
   * picker's own error id, so both are announced.
   */
  'aria-describedby'?: string;
  /** From `FormControl`. The trigger is invalid when this or `error` says so. */
  'aria-invalid'?: boolean | 'true' | 'false';
}

/**
 * Single-select picker (BAL-1132). The trigger is a BUTTON that renders only
 * the committed option or the placeholder; the search box lives inside the
 * popover, is not a form field, and its text is discarded on close. Search
 * text therefore cannot pose as a value, whether typed or autofilled.
 *
 * Use it for every "pick one of these options" field. `Autocomplete` remains
 * for multi-select (chips show the value) and for searches whose typed text
 * is the input itself (address lookup, navigation search).
 *
 * Keyboard: Enter / Space / ArrowDown open; type to filter by label,
 * description or value; arrows move; Enter chooses; Escape closes without
 * changing the value. Focus returns to the trigger on close.
 *
 * Tests: every consumer's test must call
 * `expectPickerShowsOnlyCommittedValue` from
 * `apps/web/app/__tests__/helpers/picker-contract.ts`.
 */
export function OptionPicker({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found',
  disabled = false,
  error,
  header,
  clearable = false,
  clearLabel = 'Clear selection',
  className,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: OptionPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const baseId = useId();
  // cmdk identifies rows by value; this one cannot collide with an option.
  const clearRowValue = `${baseId}-clear`;
  const errorId = `${baseId}-error`;

  // Search text lives only while the popover is open; closing discards it.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  };

  const selected = value
    ? (options.find((o) => o.value === value) ?? null)
    : null;

  // Grouped rendering when any option carries a `group` (first-seen order);
  // otherwise one unlabeled group.
  const groups = useMemo(() => {
    const buckets = new Map<string, OptionPickerOption[]>();
    for (const option of options) {
      const key = option.group ?? '';
      const bucket = buckets.get(key);
      if (bucket) bucket.push(option);
      else buckets.set(key, [option]);
    }
    return [...buckets.entries()];
  }, [options]);

  // Emits even when the option is already the shown value: a caller may show
  // a normalised or defaulted value the form does not hold yet, and choosing
  // it must write it.
  const choose = (next: OptionPickerOption) => {
    if (next.disabled) return;
    handleOpenChange(false);
    onChange(next.value);
  };

  let display: React.ReactNode;
  if (selected) {
    // Same visuals as the list row (icon + label), so the trigger reads
    // identically to the option the user picked.
    display = (
      <>
        {selected.icon}
        <span data-slot="picker-label" className="truncate">
          {selected.label}
        </span>
      </>
    );
  } else if (value) {
    // A value with no matching option still shows, so the trigger never
    // claims "nothing chosen" while the form holds something.
    display = (
      <span data-slot="picker-label" className="truncate">
        {value}
      </span>
    );
  } else {
    display = (
      <span
        data-slot="picker-placeholder"
        className="text-muted-foreground truncate"
      >
        {placeholder}
      </span>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      <Popover open={open} onOpenChange={handleOpenChange} modal>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            // Radix sets aria-controls to the popover content's id, which
            // exists while open. The popup is a dialog holding the listbox.
            aria-expanded={open}
            aria-haspopup="dialog"
            // An id means an external `<label htmlFor>` (or FormLabel) names
            // the trigger; a generic aria-label would override it. Same rule
            // as Autocomplete's input (autocomplete-desktop.tsx).
            aria-label={!id && !ariaLabelledBy ? ariaLabel : undefined}
            aria-labelledby={ariaLabelledBy}
            aria-invalid={
              !!error || ariaInvalid === true || ariaInvalid === 'true'
            }
            aria-describedby={
              [ariaDescribedBy, error ? errorId : undefined]
                .filter(Boolean)
                .join(' ') || undefined
            }
            disabled={disabled}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setOpen(true);
              }
            }}
            className={cn(
              'h-11 w-full justify-between px-3 font-normal',
              error && 'border-destructive',
            )}
          >
            <span
              data-slot="picker-value"
              className="flex min-w-0 items-center gap-2"
            >
              {display}
            </span>
            <ChevronDownIcon
              className={cn(
                'text-muted-foreground h-4 w-4 shrink-0 transition-transform',
                open && 'rotate-180',
              )}
            />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] min-w-56 p-0"
        >
          {/* Default highlight on the committed option, so Enter keeps it. */}
          <Command defaultValue={selected?.value}>
            {header && (
              <div className="text-muted-foreground border-b px-3 py-2 text-sm">
                {header}
              </div>
            )}
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              {groups.map(([group, groupOptions]) => (
                <CommandGroup key={group} heading={group || undefined}>
                  {groupOptions.map((option) => {
                    const isChosen = option.value === selected?.value;
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        keywords={[option.label, option.description ?? '']}
                        disabled={option.disabled}
                        data-checked={isChosen}
                        onSelect={() => choose(option)}
                        className="gap-2"
                      >
                        {option.icon}
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate">{option.label}</span>
                          {option.description && (
                            <span className="text-muted-foreground truncate text-xs">
                              {option.description}
                            </span>
                          )}
                        </span>
                        <CheckIcon
                          className={cn(
                            'h-4 w-4 shrink-0',
                            isChosen ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
              {/* After the options: opening highlights the committed option
                  (Command defaultValue), so Enter right after opening keeps it. */}
              {clearable && value && !search && (
                <CommandGroup>
                  <CommandItem
                    value={clearRowValue}
                    onSelect={() => {
                      handleOpenChange(false);
                      onChange('');
                    }}
                    className="text-muted-foreground"
                  >
                    {clearLabel}
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {error && (
        <p id={errorId} className="text-destructive text-sm">
          {error}
        </p>
      )}
    </div>
  );
}

OptionPicker.displayName = 'OptionPicker';
